import { LayoutItem } from "node_modules/react-grid-layout/dist";
import { DashboardWidgetDefinition } from "src/types/dashboard";
import {
  GRID_MIN_WIDTHS,
  GRID_TOTAL_COLS,
} from "src/constants/dashboard";

const buildLayoutItem = ({
  i,
  x,
  y,
  w,
  h,
  minW,
  minH,
  maxW,
  maxH,
  static: isStatic = false,
  isDraggable,
  isResizable,
  resizeHandles,
  isBounded = false,
  moved = false,
  constraints,
}: LayoutItem): LayoutItem => ({
  i,
  x,
  y,
  w,
  h,
  minW,
  minH,
  maxW,
  maxH,
  static: isStatic,
  isDraggable: isDraggable ?? !isStatic,
  isResizable: isResizable ?? !isStatic,
  resizeHandles,
  isBounded,
  moved,
  constraints,
});

export const toLayoutItem = (widget: DashboardWidgetDefinition): LayoutItem =>
  buildLayoutItem({
    i: String(widget.id),
    x: widget.grid.x,
    y: widget.grid.y,
    w: widget.grid.w,
    h: widget.grid.h,
    minW: widget.grid_constraints.min_w,
    minH: widget.grid_constraints.min_h,
    maxW: widget.grid_constraints.max_w,
    maxH: widget.grid_constraints.max_h,
    static: false,
    isDraggable: false,
    isResizable: false,
  });

/**
 * Builds one layout per breakpoint from the dashboard's saved 24-column
 * layout.
 *
 * Every breakpoint uses 24 columns (see GRID_COLS), so the saved coordinates
 * are always in range and RGL never has to clamp them. What changes with
 * screen size is how wide a widget is allowed to get: below `md` a minimum
 * width is enforced, and anything narrower than it is widened and the row
 * repacked. A quarter-width KPI becomes a half on a tablet and full width on
 * a phone, instead of shrinking until its number is unreadable.
 *
 * Widgets keep their authored order (top-to-bottom, then left-to-right), so a
 * narrow screen reads the dashboard in the same sequence as a wide one.
 */
/**
 * Repacks a layout for a narrower grid.
 *
 * Every breakpoint uses 24 columns (see GRID_COLS), so saved coordinates are
 * always in range and RGL never has to clamp them. What changes with screen
 * size is how wide a widget may be: below `md` a minimum width is enforced,
 * and anything narrower is widened and the row repacked. A quarter-width KPI
 * becomes a half on a tablet and full width on a phone, instead of shrinking
 * until its number is unreadable.
 *
 * Widgets keep their authored order (top-to-bottom, then left-to-right), so a
 * narrow screen reads the dashboard in the same sequence as a wide one.
 *
 * `minW <= 1` means "leave it exactly as authored" -- the desktop breakpoints.
 */
export const reflowLayoutForMinWidth = (
  // readonly: RGL's `Layout` is a readonly array, and nothing here mutates the
  // input -- every branch spreads into new objects.
  base: readonly LayoutItem[],
  minW: number,
): LayoutItem[] => {
  if (minW <= 1) return base.map((item) => ({ ...item }));

  let x = 0;
  let y = 0;
  let rowHeight = 0;

  return [...base]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((item) => {
      const w = Math.min(GRID_TOTAL_COLS, Math.max(minW, item.w));

      // Start a new row when this one can't take the widget.
      if (x + w > GRID_TOTAL_COLS) {
        x = 0;
        y += rowHeight;
        rowHeight = 0;
      }

      const placed: LayoutItem = {
        ...item,
        x,
        y,
        w,
        // Widget templates ship max_w: 12, which would clamp a widget we just
        // widened to full width straight back to a half. Resizing is disabled
        // wherever a reflowed layout is shown, so maxW has no other job here.
        maxW: Math.max(item.maxW ?? GRID_TOTAL_COLS, w),
      };

      x += w;
      rowHeight = Math.max(rowHeight, item.h);
      return placed;
    });
};

/** One layout per breakpoint, keyed the way ResponsiveGridLayout expects. */
export const buildResponsiveLayoutsFromLayout = (
  base: readonly LayoutItem[],
): Record<string, LayoutItem[]> => ({
  lg: reflowLayoutForMinWidth(base, GRID_MIN_WIDTHS.lg),
  md: reflowLayoutForMinWidth(base, GRID_MIN_WIDTHS.md),
  sm: reflowLayoutForMinWidth(base, GRID_MIN_WIDTHS.sm),
  xs: reflowLayoutForMinWidth(base, GRID_MIN_WIDTHS.xs),
  xxs: reflowLayoutForMinWidth(base, GRID_MIN_WIDTHS.xxs),
});

export const buildResponsiveLayouts = (
  widgets: DashboardWidgetDefinition[],
): Record<string, LayoutItem[]> =>
  buildResponsiveLayoutsFromLayout(widgets.map(toLayoutItem));

// Accepts `string | number` (not just `number`) because several KPI payloads
// (e.g. sparkle's total_count) already send a pre-formatted string like
// "64.6%" — that string is passed straight through, only a real number gets
// comma-formatted. Widening this signature (rather than casting at call
// sites) is what let SparkleWidget drop its `total_count as number` lie.
export function formatNumber(num: number | string) {
  if (num === null || num === undefined) return "";
  if (typeof num === "string") return num;

  return num.toLocaleString("en-US");
}

export const toSnakeCase = (str: string) => {
  return str
    ?.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
    )
    ?.map((x) => x.toLowerCase())
    ?.join("_");
};
